import { Component, Output, Input, HostListener, ViewChild, EventEmitter, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material';
import { ApiService } from '../../shared/api.service';
import { ImagesService } from '../../shared/images.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { SImage } from '../../shared/image.model';

@Component({
    selector: 'getty-images',
    templateUrl: 'getty.component.html'
})
export class GettyComponent {
    captionCharacterCount = 45;
    selected;
    mode = 'editorial';
    modeMap = {
        editorial: {
            label: 'Editorial',
            value: 'editorial'
        },
        creative: {
            label: 'Creative',
            value: 'creative'
        },
        all: {
            label: 'All',
            value: ''
        }
    };
    searchText = '';
    searchField = new FormControl();
    orientation = '';
    vertical = true;
    horizontal = true;
    sortOrder = 'best_match';
    error = '';
    downloadError = '';
    working = false;
    page = 1;
    masonry;
    cancel = new EventEmitter();
    last = false;
    loadingMore = false;
    excludeIds = [];

    private _canEdit = false;
    private _categories = [];
    private _id = [];
    @Input('canEdit')
    set canEdit(canEdit) {
        this._canEdit = canEdit;
    }
    get canEdit() {
        return this._canEdit;
    }
    @Input('categories')
    set categories(categories) {
        this._categories = categories;
    }
    get categories() {
        return this._categories;
    }
    @Input('id')
    set id(id) {
        this._id = id;
    }
    get id() {
        return this._id;
    }

    @ViewChild('loadmore') loadmore;
    @HostListener('window:scroll', ['$event'])
    maybeLoadMore(e) {
        if( this.loadmore && !this.loadingMore && this.imagesService.gettyImages.length > 0 ) {
            const bounding = this.loadmore.nativeElement.getBoundingClientRect();
            if( bounding.top >= 0 && (bounding.top - 1200) <= (window.innerHeight || document.documentElement.clientHeight) ) {
                this.search(true);
            }
        }
    }

    constructor(private api: ApiService, private imagesService: ImagesService, private dialog: MatDialog) {
        this.searchField.valueChanges.debounceTime(1000).subscribe(value => {
            if( value ) {
                this.search(false);
            }            
        });
    }
    ngAfterViewInit() {        
        this.searchText = this.getSearchString();
        if( this.imagesService.gettyImages.length == 0 ) {
            this.search(false);
        }
    }
    setMode(mode) {
        this.mode = mode;
        this.page = 1;
        this.imagesService.gettyImages = [];
        
        this.search(false);
    }
    setOrientation() {
        if( this.vertical && !this.horizontal ) {
            this.orientation = 'vertical';
        } else if( !this.vertical && this.horizontal ) {
            this.orientation = 'horizontal';
        } else {
            this.orientation = '';
        }
        
        this.search(false);
    }
    download(img) {
        this.downloadError = '';
        img.downloading = true;
        this.api.doAction('post', 'getty', {
            action: 'download',
            image: img,
            id: this.id
        }).subscribe(
            data => {
                const image = new SImage(data);
                this.imagesService.images.unshift(image);

                const gettyImages = this.imagesService.gettyImages;
                let i = gettyImages.length;
                while(i--) {
                    if( gettyImages[i].id == image.gettyId ) {
                        gettyImages.splice(i, 1);
                        break;
                    }
                }
                img.downloading = false;
            },
            err => {
                this.downloadError = 'We are having trouble connecting to Getty Images.';
                img.downloading = false;
                throw err;
            }
        );
    }
    getSearchString() {
        if( this.searchText ) {
            return this.searchText;
        }
        if( this.categories.length > 0 ) {
            return this.categories[0].name;
        }
        return '';
    }
    search(append) {
        if( this.working ) {
            this.cancel.emit(false);
        }
        this.error = '';
        this.working = true;
        this.last = false;
        if( append ) {
            this.page = this.imagesService.gettyImages.length > 0 ? this.page + 1 : 1;
            this.loadingMore = true;
        }
        this.api.doAction('post', 'getty', {
            action: 'search',
            page: this.page,
            mode: this.modeMap[this.mode].value,
            phrase: this.searchText,
            orientation: this.orientation,
            sortOrder: this.sortOrder,
            id: this.id
        }).debounceTime(1000).pipe(
            takeUntil(this.cancel),
            finalize(() => {
                this.working = false;
                this.loadingMore = false;
            })
        ).subscribe(
            data => {
                const images = data.images;
                if( data.result_count == 0 ) {
                    this.last = true;
                }
                if( append !== true ) {
                    this.imagesService.gettyImages = [].concat(images);
                } else {
                    for(var i = 0; i < images.length; i++) {
                        this.imagesService.gettyImages.push(images[i]);
                    }
                }
            },
            err => {
                this.error = 'We are having trouble connecting to Getty Images. ';
                throw err;
            }
        );
    }
    select(img) {
        this.selected = img;
    }
    titleChange() {
        this.selected.title = this.cleanText(this.selected.title);
    }
    cleanText(text) {
        return text.replace(/(<([^>]+)>)/ig,'');
    }
    getCharacterCount() {
        return this.cleanText(this.selected.caption).length;
    }
    editDetails(img) {
        const dialog = this.dialog.open(EditImageDialog, {
            data: img
        });
        dialog.afterClosed().subscribe(download => {
            if( download ) {
                this.download(img);
            }
        });
    }
}
@Component({
    selector: 'edit-image-dialog',
    template: `
        <h2 mat-dialog-title>Edit Image Details</h2>
        <mat-dialog-content class="getty-dialog">
            <div class="inner-wrap">
                <div class="image">
                    <img [src]="data.display_sizes[0].uri" />
                    {{data.width}}x{{data.height}}
                </div>
                <div class="input-wrap">
                    <label>Title</label>
                    <input [(ngModel)]="data.title" placeholder="Image title">
                </div>
                <div class="input-wrap">
                    <label>Caption</label>
                    <richtext
                        class="caption"
                        [(value)]="data.caption"
                        placeholder="Caption">
                    </richtext>
                </div>
            </div>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button class="button" mat-dialog-close>Cancel</button>
            <button class="button" [mat-dialog-close]="true">Import</button>
        </mat-dialog-actions>`,
})
export class EditImageDialog {
    constructor( @Inject(MAT_DIALOG_DATA) public data: any) {
        let height = (data.height / data.width) * 1200;
        data.height = Math.floor(height);
        data.width = 1200;
    }
}


// WEBPACK FOOTER //
// /opt/atlassian/pipelines/agent/build/src/swarm/src/app/slideshow/images/getty.component.ts